/**
 * API Client for Looker Embed Portal
 * 
 * Uses fetch with Amplify auth tokens for API calls.
 * Reads API endpoint from Amplify outputs configuration.
 */

import { fetchAuthSession } from 'aws-amplify/auth';
import outputs from '../../amplify_outputs.json';

// API base URL from Amplify outputs (custom.apiEndpoint from backend.ts)
// Remove trailing slash to prevent double-slash issues when concatenating paths
const API_BASE_URL = (outputs.custom?.apiEndpoint || '').replace(/\/+$/, '');

/**
 * Get current ID token from Amplify for API Gateway Cognito Authorizer
 * Note: Cognito User Pool Authorizers expect ID tokens, not access tokens
 */
export async function getAccessToken(): Promise<string | undefined> {
  try {
    const session = await fetchAuthSession();
    // Use idToken for API Gateway Cognito Authorizer (not accessToken)
    const token = session.tokens?.idToken?.toString();
    if (!token) {
      console.warn('No ID token available - user may not be authenticated');
    }
    return token;
  } catch (error) {
    console.error('Error getting ID token:', error);
    return undefined;
  }
}

/**
 * Make authenticated API request
 */
async function apiRequest<T>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const token = await getAccessToken();
  
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || `Request failed: ${response.status}`);
  }

  return response.json();
}

// ============================================================================
// Dashboard API
// ============================================================================

export interface Dashboard {
  id: string;
  title: string;
  description: string;
  viewCount: number;
  favoriteCount: number;
  creatorName: string;
  thumbnailUrl: string;
  embedPath: string;
}

export interface DashboardFolder {
  id: string;
  name: string;
  dashboards: Dashboard[];
}

export const dashboardApi = {
  /**
   * Get all dashboards as a flat array (legacy method for backward compatibility)
   * Note: Backend now returns folder-grouped structure, this method flattens it
   */
  getDashboards: async (): Promise<Dashboard[]> => {
    const response = await apiRequest<{ folders: DashboardFolder[] }>('/dashboards');
    return response.folders.flatMap(folder => folder.dashboards);
  },

  /**
   * Get dashboards grouped by folder (both admin and viewer responses)
   * @param sudoUserId - Optional viewer ID for admin impersonation
   */
  getDashboardsByFolder: async (sudoUserId?: string): Promise<DashboardFolder[]> => {
    const params = sudoUserId ? `?sudo=${encodeURIComponent(sudoUserId)}` : '';
    const response = await apiRequest<{ folders: DashboardFolder[] }>(`/dashboards${params}`);
    return response.folders;
  },

  /**
   * Get dashboard thumbnail URL (public, no auth required)
   */
  getThumbnailUrl: (dashboardId: string): string => {
    return `${API_BASE_URL}/dashboards/${dashboardId}/thumbnail`;
  },
};

// ============================================================================
// Embed API
// ============================================================================

export interface EmbedSessionResponse {
  authentication_token?: string;
  authentication_token_ttl?: number;
  navigation_token?: string;
  navigation_token_ttl?: number;
  api_token?: string;
  api_token_ttl?: number;
  session_reference_token_ttl?: number;
  theme_name?: string | null;
}

export const embedApi = {
  /**
   * Acquire cookieless embed session
   * @param sudoUserId - Optional viewer ID for admin impersonation
   */
  acquireSession: async (sudoUserId?: string): Promise<EmbedSessionResponse> => {
    const params = sudoUserId ? `?sudo=${encodeURIComponent(sudoUserId)}` : '';
    return apiRequest<EmbedSessionResponse>(`/embed/acquire-session${params}`);
  },

  /**
   * Generate new tokens for existing session
   * @param sudoUserId - Optional viewer ID for admin impersonation
   */
  generateTokens: async (tokens: {
    api_token?: string;
    navigation_token?: string;
  }, sudoUserId?: string): Promise<EmbedSessionResponse> => {
    const params = sudoUserId ? `?sudo=${encodeURIComponent(sudoUserId)}` : '';
    return apiRequest<EmbedSessionResponse>(`/embed/generate-tokens${params}`, {
      method: 'PUT',
      body: JSON.stringify(tokens),
    });
  },

  /**
   * Get configured Looker host
   */
  getLookerHost: async (): Promise<string> => {
    const response = await apiRequest<{ host: string }>('/embed/looker-host');
    return response.host;
  },

  /**
   * Validate that a dashboard ID is accessible by the current user
   * @param dashboardId - Dashboard ID or slug to validate
   * @param sudoUserId - Optional viewer ID for admin impersonation
   */
  validateDashboard: async (dashboardId: string, sudoUserId?: string): Promise<{ allowed: boolean }> => {
    const params = new URLSearchParams({ dashboardId });
    if (sudoUserId) params.set('sudo', sudoUserId);
    return apiRequest<{ allowed: boolean }>(`/embed/validate-dashboard?${params.toString()}`);
  },
};

// ============================================================================
// User API (Admin only)
// ============================================================================

export interface User {
  id: string;
  cognitoSub: string;
  externalUserId?: string;
  email?: string;
  firstName: string;
  lastName: string;
  userType: 'admin' | 'viewer';
  permissions?: string[];
  models?: string[];
  groupIds?: number[];
  externalGroupId?: string;
  userAttributes?: Record<string, unknown>;
  accessFilters?: Record<string, unknown>;
  folderIds?: string[];
  themeName?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateUserRequest {
  username: string;
  password: string;
  firstName: string;
  lastName: string;
  userType: 'admin' | 'viewer';
  permissions?: string[];
  models?: string[];
  groupIds?: number[];
  externalGroupId?: string;
  userAttributes?: Record<string, unknown>;
  accessFilters?: Record<string, unknown>;
  folderIds?: string[];
  themeName?: string;
}

export interface UpdateUserRequest {
  firstName?: string;
  lastName?: string;
  userType?: 'admin' | 'viewer';
  newPassword?: string;
  resetMfa?: boolean;
  permissions?: string[];
  models?: string[];
  groupIds?: number[];
  externalGroupId?: string;
  userAttributes?: Record<string, unknown>;
  accessFilters?: Record<string, unknown>;
  folderIds?: string[];
  themeName?: string;
}

export interface SyncUsersResponse {
  message: string;
  synced: number;
  total: number;
  users: Array<{ id: string; email: string; firstName: string; lastName: string }>;
}

export const userApi = {
  /**
   * Get all users
   */
  getUsers: async (): Promise<User[]> => {
    return apiRequest<User[]>('/users');
  },

  /**
   * Sync Cognito users to DynamoDB
   * Creates EmbedUser records for Cognito users that don't have one
   */
  syncUsers: async (): Promise<SyncUsersResponse> => {
    return apiRequest<SyncUsersResponse>('/users/sync', {
      method: 'POST',
    });
  },

  /**
   * Get user by ID
   */
  getUser: async (id: string): Promise<User> => {
    return apiRequest<User>(`/users/${id}`);
  },

  /**
   * Create new user
   */
  createUser: async (data: CreateUserRequest): Promise<{ user: User }> => {
    return apiRequest<{ user: User }>('/users', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * Update user
   */
  updateUser: async (id: string, data: UpdateUserRequest): Promise<void> => {
    await apiRequest(`/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  /**
   * Delete user
   */
  deleteUser: async (id: string): Promise<void> => {
    await apiRequest(`/users/${id}`, {
      method: 'DELETE',
    });
  },
};

// ============================================================================
// Config API (Admin only)
// ============================================================================

export interface LookerConfig {
  lookerHost: string;
  clientId: string;
  clientSecret: string;
  frontendUrl: string;
}

export interface LookerModel {
  name: string;
  label: string;
}

export interface LookerUserAttribute {
  id: number;
  name: string;
  label: string;
  type: string;
  default_value: string | null;
}

export interface LookerTheme {
  id: string;
  name: string;
}

export const configApi = {
  /**
   * Get available Looker models
   */
  getModels: async (): Promise<LookerModel[]> => {
    return apiRequest<LookerModel[]>('/config/models');
  },

  /**
   * Get available Looker user attributes (non-system only)
   */
  getUserAttributes: async (): Promise<LookerUserAttribute[]> => {
    return apiRequest<LookerUserAttribute[]>('/config/user-attributes');
  },

  /**
   * Get available Looker themes
   */
  getThemes: async (): Promise<LookerTheme[]> => {
    return apiRequest<LookerTheme[]>('/config/themes');
  },
};
